// src/store/index.ts (Updated for T-23 Local Persistence)
// This file replaces the existing store/index.ts
/**
 * Main Redux store configuration
 * Combines all slices and configures middleware
 * UPDATED: Added T-23 draft persistence support
 */

import { configureStore } from '@reduxjs/toolkit';
import { RootState } from '../types/redux';

// Import all slice reducers
import viewportReducer from './slices/viewportSlice';
import numberLinesReducer from './slices/numberLinesSlice';
import secondaryLineReducer from './slices/secondaryLineSlice';
import labelVisibilityReducer from './slices/labelVisibilitySlice';
import customTicksReducer from './slices/customTicksSlice';
import jumpIndicatorsReducer from './slices/jumpIndicatorsSlice';
import multipliersReducer from './slices/multiplierSlice';
import annotationsReducer from './slices/annotationsSlice';
import expressionsReducer from './slices/expressionsSlice';
import constantsReducer from './slices/constantsSlice';
import equivalentFractionsReducer from './slices/equivalentFractionsSlice';
import uiReducer from './slices/uiSlice';
import shareReducer from './slices/shareSlice';
import preferencesReducer from './slices/preferencesSlice';
import draftReducer from './slices/draftSlice';
import toolModeReducer from './slices/toolModeSlice';
import coordinateGridReducer from './slices/coordinateGridSlice';
import dnlToolsReducer from './slices/dnlToolsSlice';

// Import T-23 middleware
import { autoSaveMiddleware, setupAutoSaveEventListeners } from './middleware/autoSaveMiddleware';



// Import T-23 utilities
import { 
  loadDraftFromStorage, 
  isDraftAvailable, 
  getDraftMetadata,
  type LoadDraftResult 
} from '../lib/localPersistence';
import { 
  initializeDraftState, 
  setAutoSaveEnabled, 
  setLoadedFromDraft,
  completeSave,
  completeLoad
} from './slices/draftSlice';

// =============================================================================
// DRAFT RESTORATION LOGIC
// =============================================================================

/**
 * Attempt to restore draft on app startup
 * Returns enhanced preloaded state if draft exists and should be restored
 */
function attemptDraftRestoration(basePreloadedState?: Partial<RootState>): {
  preloadedState: Partial<RootState>;
  shouldShowBanner: boolean;
  draftMetadata: any;
} {
  const result = {
    preloadedState: basePreloadedState || {},
    shouldShowBanner: false,
    draftMetadata: null,
  };

  try {
    // Check if auto-save is enabled in preferences (default true)
    const autoSaveEnabled = result.preloadedState.preferences?.autoSave !== false;
    
    if (!autoSaveEnabled) {
      console.debug('Draft restoration skipped: auto-save disabled in preferences');
      return result;
    }

    // Check if draft exists
    if (!isDraftAvailable()) {
      console.debug('No draft available for restoration');
      return result;
    }

    // Load draft metadata
    const metadata = getDraftMetadata();
    if (!metadata) {
      console.debug('Draft metadata unavailable');
      return result;
    }

    // Load the actual draft data
    const loadResult = loadDraftFromStorage();
    if (!loadResult.success || !loadResult.data) {
      console.warn('Failed to load draft:', loadResult.error);
      return result;
    }

    console.info('Draft restoration successful:', {
      timestamp: metadata.timestamp,
      size: metadata.size,
      slices: metadata.stateSlices,
    });

    // Merge draft data with base preloaded state
    result.preloadedState = {
      ...basePreloadedState,
      ...loadResult.data,
      // Ensure draft slice is properly initialized
      draft: {
        lastSaved: metadata.timestamp,
        autoSaveEnabled: true,
        isDraftAvailable: true,
        loadedFromDraft: true,
        draftMetadata: metadata,
        lastSaveResult: null,
        lastLoadResult: loadResult,
        lastError: null,
        isSaving: false,
        isLoading: false,
        showRestorationBanner: true,
      },
    };

    result.shouldShowBanner = true;
    result.draftMetadata = metadata;

  } catch (error) {
    console.warn('Draft restoration failed:', error);
  }

  return result;
}

// =============================================================================
// STORE CONFIGURATION
// =============================================================================

/**
 * Configure the Redux store with all slices and middleware
 */
export function configureAppStore(preloadedState?: Partial<RootState>, options: {
  enableDraftRestoration?: boolean;
  enableAutoSave?: boolean;
  enableAutoSaveLogging?: boolean;
} = {}) {
  const {
    enableDraftRestoration = true,
    enableAutoSave = true,
    enableAutoSaveLogging = false,
  } = options;

  // Handle draft restoration
  let finalPreloadedState = preloadedState;
  let draftRestored = false;
  let draftMetadata = null;

  if (enableDraftRestoration) {
    const restorationResult = attemptDraftRestoration(preloadedState);
    finalPreloadedState = restorationResult.preloadedState;
    draftRestored = restorationResult.shouldShowBanner;
    draftMetadata = restorationResult.draftMetadata;
  }

  const store = configureStore({
    reducer: {
      viewport: viewportReducer,
      numberLines: numberLinesReducer,
      secondaryLine: secondaryLineReducer,
      equivalentFractions: equivalentFractionsReducer,
      labelVisibility: labelVisibilityReducer,
      customTicks: customTicksReducer,
      jumpIndicators: jumpIndicatorsReducer,
      multipliers: multipliersReducer,
      annotations: annotationsReducer,
      expressions: expressionsReducer,
      constants: constantsReducer,
      ui: uiReducer,
      share: shareReducer,
      preferences: preferencesReducer,
      draft: draftReducer,
      toolMode: toolModeReducer,
      coordinateGrid: coordinateGridReducer,
      dnlTools: dnlToolsReducer,
    },
    preloadedState: finalPreloadedState,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types from serializability checks
          ignoredActions: [
            'persist/PERSIST', 
            'persist/REHYDRATE',
            'draft/completeSave',
            'draft/completeLoad',
          ],
          // Ignore these paths in the state
          ignoredPaths: [
            'customTicks.ticks.*.createdAt',
            'draft.lastSaveResult',
            'draft.lastLoadResult',
            'draft.draftMetadata',
          ],
        },
      });

      // Add auto-save middleware if enabled
      if (enableAutoSave) {
        console.log('✅ Auto-save middleware enabled'); // Debug log
        middleware.push(autoSaveMiddleware);
        console.log('🔧 Middleware stack:', middleware.length, 'middlewares'); // Debug log
        
        // Test that middleware is properly added
        console.log('🧪 Testing middleware application...');
        const testStore = configureStore({
          reducer: { test: (state = {}, action) => state },
          middleware: () => middleware,
        });
        console.log('✅ Test store created with middleware');
      } else {
        console.log('❌ Auto-save middleware disabled'); // Debug log
      }



      return middleware;
    },
    devTools: process.env.NODE_ENV !== 'production',
  });

  // Initialize draft state after store creation
  if (enableDraftRestoration || enableAutoSave) {
    // Initialize draft slice with current state
    store.dispatch(initializeDraftState());
    
    // Sync auto-save preference
    const currentPrefs = store.getState().preferences;
    store.dispatch(setAutoSaveEnabled(currentPrefs.autoSave));
    
    // Set up browser event listeners for auto-save
    if (enableAutoSave) {
      setupAutoSaveEventListeners(store);
    }
    
    // If draft was restored, ensure the flag is set
    if (draftRestored) {
      store.dispatch(setLoadedFromDraft(true));
    }
  }

  return store;
}

// =============================================================================
// STORE INSTANCE AND UTILITIES
// =============================================================================

/**
 * Main store instance with draft restoration enabled
 */
export const store = configureAppStore();

/**
 * Create store for testing without draft restoration
 */
export function createTestStore(preloadedState?: Partial<RootState>) {
  return configureAppStore(preloadedState, {
    enableDraftRestoration: false,
    enableAutoSave: false,
  });
}

/**
 * Create store with custom configuration
 */
export function createCustomStore(
  preloadedState?: Partial<RootState>,
  options?: {
    enableDraftRestoration?: boolean;
    enableAutoSave?: boolean;
    enableAutoSaveLogging?: boolean;
  }
) {
  return configureAppStore(preloadedState, options);
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

// Infer types from the store
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;

// Re-export RootState type (needs to include draft slice now)
export type { RootState } from '../types/rootState';

// =============================================================================
// HOOK EXPORTS
// =============================================================================

// Re-export hooks
export { useAppSelector, useAppDispatch } from './hooks';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Clear all stored data and reset to fresh state
 * Useful for "Start Fresh" functionality
 */
export function resetToFreshState() {
  // Clear localStorage
  try {
    localStorage.removeItem('nl:draft');
    localStorage.removeItem('nl:preferences'); // Reset preferences too if desired
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
  
  // Reload the page to get fresh state
  window.location.reload();
}

/**
 * Get draft status information
 */
export function getDraftStatus() {
  return {
    available: isDraftAvailable(),
    metadata: getDraftMetadata(),
  };
}

/**
 * Force save current state as draft
 */
export async function forceSaveDraft() {
  const state = store.getState();
      const { saveDraftToStorage } = await import('../lib/localPersistence');
    return await saveDraftToStorage(state);
}